import React, { CSSProperties } from 'react'
import type { Column, CellInfo, RowInfo } from 'react-table-6'
import type { History } from 'history'
import type { match } from 'react-router-dom'

export interface ExtRowInfo<T> extends RowInfo {
  row: T
  original: T
}

export interface ExtCellInfo<T> extends CellInfo {
  row: T
  original: T
}

export interface ExtColumn<T> extends Column<T> {
  columnType?: ColumnType
  disabled?: boolean | ((row: T) => boolean)
  isSelector?: boolean
  template?: (
    row: ExtCellInfo<T>,
    column: ExtColumn<T>,
    defaultTemplate?: React.ReactNode
  ) => React.ReactNode
  border?: string
  Cell?:
    | ((cellInfo: ExtCellInfo<T>, column?: any) => React.ReactNode)
    | React.ReactNode
}

export enum ColumnExtraType {
  iconGraph = 'iconGraph'
}

export enum ColumnExternalType {
  text = 'text',
  number = 'number',
  datetime = 'datetime',
  boolean = 'boolean'
}

export enum ColumnInternalType {
  selector = 'selector',
  selectFilter = 'selectFilter',
  severity = 'severity',
  actions = 'actions',
  button = 'button'
}

export type ColumnType =
  | ColumnInternalType
  | ColumnExternalType
  | ColumnExtraType

export interface ActionOption<T> {
  title?: string
  className?: string
  iconClassName?: string
  icon?: CAMIconName
  style?: CSSProperties
  headerStyle?: CSSProperties
  onClick: () => void
  template?: (
    row: ExtCellInfo<T>,
    column: ColumnProps<T>,
    action: ActionOption<T>,
    defaultTemplate: JSX.Element
  ) => JSX.Element
  hide?: (rowData: T, row: ExtCellInfo<T>, column: ColumnProps<T>) => boolean
  handler: (
    rowData: T,
    columns: ColumnProps<T>[],
    resolvedState: () => any
  ) => void
  disabled?: (rowData: T) => boolean | boolean
  description?: (rowData: T) => string | string
}

export interface ColumnProps<T, P extends { [K in keyof P]?: string } = {}> {
  subColumns?: ColumnProps<T, P>[]
  title?: string
  field?: keyof T
  className?: string
  descriptionCell?: string
  descriptionColumn?: string
  type?: ColumnType
  severityOptions?: {
    high: (row: ExtCellInfo<T>) => boolean
    medium: (row: ExtCellInfo<T>) => boolean
    low: (row: ExtCellInfo<T>) => boolean
  }
  format?: string
  style?: CSSProperties
  hide?: boolean
  sortable?: boolean
  width?: number
  disabled?: boolean | ((row: T) => boolean)
  clickable?: (data: T) => boolean | string | number
  editable?: ((data: T, column: ColumnProps<T>) => boolean) | boolean
  resizable?: boolean
  onClick?: (
    rowData: T,
    column?: ColumnProps<T>,
    history?: History,
    match?: match<P>
  ) => void
  onChanged?: (
    innerText: string,
    data: T,
    column: ColumnProps<T>,
    e: React.FocusEvent<HTMLDivElement>
  ) => void
  template?: (
    row: ExtCellInfo<T>,
    column: ColumnProps<T>,
    defaultTemplate?: React.ReactNode
  ) => React.ReactNode
  headerTemplate?: (
    column: ColumnProps<T>,
    defaultColumnProps?: ExtColumn<T>
  ) => JSX.Element
  // Turns this column into a special column for specifying expander position and options in your column definitions.
  expander?: boolean
  expanderOptions?: {
    // the rendering of the expander itself via the `expanderTemplate` property
    expanderTemplate?: (
      { original, isExpanded }: { original: T; isExpanded: boolean },
      column: ColumnProps<T>
    ) => JSX.Element
    // the rendering of the content in Sub Component
    contentTemplate: (
      rowInfo: ExtRowInfo<T>,
      expanderColumn?: ColumnProps<T>
    ) => JSX.Element
  }
  actionOptions?: ActionOption<T>[]
  headerStyle?: CSSProperties
}
